import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Upload, X, Loader2 } from "lucide-react";
import api, { imageUrl } from "../lib/api";
import Seo from "../components/Seo";

const STATUS = ["Ongoing", "Upcoming", "New Launch", "RERA Approved", "Completed"];
const inputCls = "w-full rounded-lg border border-stone-200 px-3.5 py-2.5 text-sm outline-none focus:border-brand bg-white";

const lines = (t) => (t || "").split("\n").map((l) => l.trim()).filter(Boolean);
const pairs = (t, keys) => lines(t).map((l) => {
  const parts = l.split("|").map((s) => s.trim());
  const o = {};
  keys.forEach((k, i) => (o[k] = parts[i] || ""));
  return o;
});
const toLines = (arr) => (arr || []).join("\n");
const toPairs = (arr, keys) => (arr || []).map((o) => keys.map((k) => o[k] || "").join(" | ")).join("\n");

const Field = ({ label, hint, children }) => (
  <div>
    <label className="text-sm font-medium text-stone-700">{label}</label>
    {hint && <p className="text-xs text-stone-400 mb-1">{hint}</p>}
    <div className="mt-1.5">{children}</div>
  </div>
);

const ProjectForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const editing = Boolean(id);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const [f, setF] = useState({
    title: "", tagline: "", location: "", rera_id: "", status: "Ongoing", developer: "Satyam Real Estate",
    price_from: "On Request", hero_image: "", video_url: "", gallery: [], overview_heading: "", overview_text: "",
    featured: false, order: 0,
    statsText: "", highlightsText: "", featuresText: "", amenitiesText: "", facilitiesText: "", configsText: "", connText: "",
  });

  useEffect(() => {
    if (!editing) return;
    api.get("/projects").then((r) => {
      const p = r.data.find((x) => x._id === id);
      if (p) setF({
        ...p,
        statsText: toPairs(p.stats, ["value", "label"]),
        highlightsText: toLines(p.highlights),
        featuresText: toPairs(p.features, ["title", "desc"]),
        amenitiesText: toLines(p.amenities),
        facilitiesText: toLines(p.facilities),
        configsText: toPairs(p.configs, ["name", "size", "price"]),
        connText: toPairs(p.connectivity, ["place", "time"]),
      });
      setLoading(false);
    });
  }, [id, editing]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const upload = async (e, target) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(target);
    try {
      const paths = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        paths.push(data.path);
      }
      if (target === "hero") set("hero_image", paths[0]);
      else set("gallery", [...f.gallery, ...paths]);
      toast.success("Uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading("");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      title: f.title, tagline: f.tagline, location: f.location, rera_id: f.rera_id, status: f.status,
      developer: f.developer, price_from: f.price_from, hero_image: f.hero_image, video_url: f.video_url, gallery: f.gallery,
      overview_heading: f.overview_heading, overview_text: f.overview_text,
      featured: f.featured, order: Number(f.order) || 0,
      stats: pairs(f.statsText, ["value", "label"]),
      highlights: lines(f.highlightsText),
      features: pairs(f.featuresText, ["title", "desc"]),
      amenities: lines(f.amenitiesText),
      facilities: lines(f.facilitiesText),
      configs: pairs(f.configsText, ["name", "size", "price"]),
      connectivity: pairs(f.connText, ["place", "time"]),
    };
    try {
      if (editing) await api.put(`/projects/${id}`, payload);
      else await api.post("/projects", payload);
      toast.success(editing ? "Project updated" : "Project created");
      navigate("/admin");
    } catch {
      toast.error("Save failed. Check required fields.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="max-w-4xl mx-auto px-5 py-20"><div className="h-96 rounded-2xl bg-stone-100 animate-pulse" /></div>;

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 py-10">
      <Seo title={`${editing ? "Edit" : "Add"} Project | Admin`} description="Manage project" />
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-brand mb-5"><ArrowLeft size={16} /> Back to dashboard</Link>
      <h1 className="font-heading text-3xl text-stone-900 mb-8">{editing ? "Edit Project" : "Add New Project"}</h1>

      <form onSubmit={submit} className="space-y-8" data-testid="project-form">
        <section className="bg-white rounded-2xl border border-stone-200 p-6 space-y-5">
          <h2 className="font-heading text-xl text-stone-900">Basics</h2>
          <Field label="Project Title *"><input required value={f.title} onChange={(e) => set("title", e.target.value)} className={inputCls} data-testid="pf-title" /></Field>
          <Field label="Tagline"><textarea rows={2} value={f.tagline} onChange={(e) => set("tagline", e.target.value)} className={inputCls} data-testid="pf-tagline" /></Field>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Location"><input value={f.location} onChange={(e) => set("location", e.target.value)} className={inputCls} data-testid="pf-location" placeholder="e.g. Delhi Road, Jaipur" /></Field>
            <Field label="RERA ID"><input value={f.rera_id} onChange={(e) => set("rera_id", e.target.value)} className={inputCls} data-testid="pf-rera" /></Field>
            <Field label="Status"><select value={f.status} onChange={(e) => set("status", e.target.value)} className={inputCls} data-testid="pf-status">{STATUS.map((s) => <option key={s}>{s}</option>)}</select></Field>
            <Field label="Developer"><input value={f.developer} onChange={(e) => set("developer", e.target.value)} className={inputCls} data-testid="pf-developer" /></Field>
            <Field label="Price From"><input value={f.price_from} onChange={(e) => set("price_from", e.target.value)} className={inputCls} data-testid="pf-price" /></Field>
            <Field label="Display Order"><input type="number" value={f.order} onChange={(e) => set("order", e.target.value)} className={inputCls} data-testid="pf-order" /></Field>
          </div>
          <label className="flex items-center gap-2.5 text-sm text-stone-700 cursor-pointer">
            <input type="checkbox" checked={f.featured} onChange={(e) => set("featured", e.target.checked)} className="w-4 h-4 accent-[#C87968]" data-testid="pf-featured" /> Featured project
          </label>
        </section>

        <section className="bg-white rounded-2xl border border-stone-200 p-6 space-y-5">
          <h2 className="font-heading text-xl text-stone-900">Overview</h2>
          <Field label="Overview Heading"><input value={f.overview_heading} onChange={(e) => set("overview_heading", e.target.value)} className={inputCls} data-testid="pf-oh" /></Field>
          <Field label="Overview Text"><textarea rows={5} value={f.overview_text} onChange={(e) => set("overview_text", e.target.value)} className={inputCls} data-testid="pf-ot" /></Field>
          <Field label="Video URL (YouTube embed)" hint="e.g. https://www.youtube.com/embed/VIDEO_ID"><input value={f.video_url} onChange={(e) => set("video_url", e.target.value)} className={inputCls} data-testid="pf-video" placeholder="https://www.youtube.com/embed/..." /></Field>
        </section>

        <section className="bg-white rounded-2xl border border-stone-200 p-6 space-y-4">
          <h2 className="font-heading text-xl text-stone-900">Images</h2>
          <Field label="Hero Image">
            <div className="flex items-center gap-4">
              {f.hero_image && <img src={imageUrl(f.hero_image)} alt="" className="w-28 h-20 rounded-lg object-cover border border-stone-200" />}
              <label className="rounded-lg border-2 border-dashed border-stone-300 px-5 py-3 cursor-pointer hover:border-brand transition-colors flex items-center gap-2 text-sm text-stone-500" data-testid="pf-hero-upload">
                {uploading === "hero" ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />} Upload
                <input type="file" accept="image/*" hidden onChange={(e) => upload(e, "hero")} />
              </label>
            </div>
          </Field>
          <Field label="Gallery">
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {f.gallery.map((img, i) => (
                <div key={`${img}-${i}`} className="relative group aspect-[4/3] rounded-lg overflow-hidden border border-stone-200">
                  <img src={imageUrl(img)} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => set("gallery", f.gallery.filter((_, x) => x !== i))}
                    className="absolute top-1 right-1 w-6 h-6 grid place-items-center rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity"><X size={14} /></button>
                </div>
              ))}
              <label className="aspect-[4/3] rounded-lg border-2 border-dashed border-stone-300 grid place-items-center cursor-pointer hover:border-brand transition-colors" data-testid="pf-gallery-upload">
                {uploading === "gallery" ? <Loader2 className="animate-spin text-brand" /> : <div className="text-center text-stone-400"><Upload size={20} className="mx-auto" /><span className="text-xs">Add</span></div>}
                <input type="file" accept="image/*" multiple hidden onChange={(e) => upload(e, "gallery")} />
              </label>
            </div>
          </Field>
        </section>

        <section className="bg-white rounded-2xl border border-stone-200 p-6 space-y-5">
          <h2 className="font-heading text-xl text-stone-900">Content</h2>
          <p className="text-xs text-stone-500 -mt-3">For lists, put one item per line. Use " | " to separate columns as shown in each hint.</p>
          <Field label="Stats" hint='One per line: Value | Label   (e.g. 500+ | Happy Families)'><textarea rows={4} value={f.statsText} onChange={(e) => set("statsText", e.target.value)} className={inputCls} data-testid="pf-stats" /></Field>
          <Field label="Highlights" hint="One per line"><textarea rows={4} value={f.highlightsText} onChange={(e) => set("highlightsText", e.target.value)} className={inputCls} data-testid="pf-highlights" /></Field>
          <Field label="Key Features" hint="One per line: Title | Description"><textarea rows={5} value={f.featuresText} onChange={(e) => set("featuresText", e.target.value)} className={inputCls} data-testid="pf-features" /></Field>
          <Field label="Amenities" hint="One per line"><textarea rows={4} value={f.amenitiesText} onChange={(e) => set("amenitiesText", e.target.value)} className={inputCls} data-testid="pf-amenities" /></Field>
          <Field label="Facilities" hint="One per line"><textarea rows={4} value={f.facilitiesText} onChange={(e) => set("facilitiesText", e.target.value)} className={inputCls} data-testid="pf-facilities" /></Field>
          <Field label="Configurations" hint="One per line: Type | Size | Price"><textarea rows={4} value={f.configsText} onChange={(e) => set("configsText", e.target.value)} className={inputCls} data-testid="pf-configs" /></Field>
          <Field label="Location & Connectivity" hint="One per line: Place | Time"><textarea rows={4} value={f.connText} onChange={(e) => set("connText", e.target.value)} className={inputCls} data-testid="pf-conn" /></Field>
        </section>

        <div className="flex gap-3">
          <button type="submit" disabled={saving} data-testid="pf-submit"
            className="rounded-full bg-brand text-white px-8 py-3 text-sm font-semibold hover:bg-brand-dark transition-colors disabled:opacity-60">
            {saving ? "Saving…" : editing ? "Update Project" : "Create Project"}
          </button>
          <Link to="/admin" className="rounded-full border border-stone-200 px-8 py-3 text-sm font-medium text-stone-600 hover:bg-stone-100 transition-colors">Cancel</Link>
        </div>
      </form>
    </div>
  );
};

export default ProjectForm;
